import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";
import { createContext, useContext, type ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * The design ships two tab treatments and they are not interchangeable:
 *
 *   pill       a segmented control on a `subtle` tray — the Brain tab row, the
 *              journal-detail and capture-review "Journal / HQ" switches.
 *   underline  a rule of Oswald labels with a 2.5px brand underline — the
 *              crew "Roster / Reports" sub-tabs.
 *
 * `underline` is deliberately NOT uppercased: the source renders it as typed
 * ("Roster", "Reports") with no `text-transform`, unlike every other Oswald
 * label in the app. Its underline is always painted — transparent when
 * inactive — and pulled up by `-1.5px` so it covers the row's own bottom rule
 * without the label ever shifting.
 */
type TabsVariant = "pill" | "underline";

const TabsVariantContext = createContext<TabsVariant>("pill");

const tabsListVariants = cva("", {
  variants: {
    variant: {
      pill: "inline-flex items-center gap-1.5 rounded-input bg-subtle p-1",
      underline: "flex items-center gap-[26px] border-b-[1.5px] border-border",
    },
  },
  defaultVariants: { variant: "pill" },
});

const tabsTriggerVariants = cva(
  [
    "cursor-pointer whitespace-nowrap bg-transparent",
    "font-display transition-colors disabled:pointer-events-none",
    "disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        pill: [
          "rounded-control px-6 py-[9px] text-[0.9rem] font-bold uppercase",
          "tracking-[0.03em] text-muted",
          "data-[state=active]:bg-surface data-[state=active]:text-ink",
          "data-[state=active]:shadow-tab",
        ],
        underline: [
          "-mb-[1.5px] border-b-[2.5px] border-transparent",
          "px-0.5 py-2.5 text-[1.02rem] font-semibold",
          "tracking-[0.01em] text-muted",
          "data-[state=active]:border-brand data-[state=active]:font-bold",
          "data-[state=active]:text-ink",
        ],
      },
    },
    defaultVariants: { variant: "pill" },
  },
);

type TabsProps = ComponentProps<typeof TabsPrimitive.Root> & {
  /** Chosen once on the root; `TabsList` and `TabsTrigger` inherit it. */
  variant?: TabsVariant;
};

export function Tabs({ className, variant = "pill", ...props }: TabsProps) {
  return (
    <TabsVariantContext value={variant}>
      <TabsPrimitive.Root data-slot="tabs" className={className} {...props} />
    </TabsVariantContext>
  );
}

/**
 * The design puts the row's spacing on the container it lives in (22px under
 * the underline row, 14–16px under a pill group), so no margin is baked in
 * here — the screen supplies it.
 */
export function TabsList({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.List>) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

export function TabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}

export function TabsContent({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("outline-none", className)}
      {...props}
    />
  );
}

export { tabsListVariants, tabsTriggerVariants };
export type { TabsProps, TabsVariant };
